import { Navigate } from 'react-router-dom';
import { useBetterAuth } from '../../hooks/useAuth';
import { resolveLandingPath } from '../layout/sidebar-config';
import { AuthLoadingFallback } from './AuthLoadingFallback';

/**
 * Resolves the landing route from the caller's own permissions.
 *
 * Two distinct "not ready yet" states must both hold the redirect back:
 *   - `loading` — the auth context is still hydrating (owned by `initAuth`).
 *   - `user.permissions === undefined` — auth is hydrated but the
 *     `/api/permissions` snapshot has not arrived. `signIn` applies the auth
 *     state (flipping `isAuthenticated`) BEFORE awaiting `loadPermissions`, so
 *     every password login commits at least one render in this window.
 *
 * Collapsing the second state into "zero permissions" would resolve to
 * `/unauthorized` — a top-level route outside every guard, which nothing
 * re-navigates once the grants land. That turns a one-round-trip race into a
 * permanent 403. Same contract as `PermissionGuard`; `full` variant because
 * this component owns the whole viewport on the catch-all route.
 */
export function LandingRedirect() {
  const { user, isAdmin, loading } = useBetterAuth();
  const perms = user?.permissions;

  if (loading || perms === undefined) {
    return <AuthLoadingFallback variant="full" />;
  }

  return <Navigate to={resolveLandingPath(perms, isAdmin)} replace />;
}
